// src/pages/onboarding/shared/steps/StepCategory.jsx
import React, { useMemo, useState } from "react";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card.jsx";
import {
  BASIC_DIALOG_ACTION_CLASS,
  BASIC_DIALOG_SECONDARY_ACTION_CLASS,
  BasicDialogFrame,
} from "@/components/ui/BasicDialog";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { writeLastRole } from "@/lib/roleCache";
import { persistPharmacyOnboardingIdentity } from "@/services/pharmacyOnboardingIdentityService.js";
import { categoryVisible, PUBLIC_ACCOUNT_ROLES } from "@/lib/publicSignupJourney.mjs";

const CATS = new Set(["doctor", "patient", "pharmacy", "research", "employer", "moh", "insurers"]);
const TEMP_DISABLED_CATS = new Set(["employer", "insurers"]);
const INVITATION_REQUIRED_CATS = new Set(["pharmacy", "research"]);
const GOVERNED_ACCESS_CATS = new Set(["moh"]);
const PATIENT_SELECTION_SESSION_KEY = "afrimed:onboarding:selected_patient_session";
const CLINICIAN_ORIENTATION_COPY =
  "AfriMed™ needs a few basic details to create your clinician workspace safely. Clinical features, patient access, bookings, prescriptions, and public discovery remain locked until your professional profile is completed and reviewed where required.";
const PATIENT_ORIENTATION_COPY =
  "AfriMed™ needs a few basic details to personalise your healthcare experience safely. Appointments, prescriptions, your health record, and other healthcare services become available when your profile is completed.";

const ORIENTATION_DIALOG_COPY = {
  doctor: {
    title: "Clinician setup",
    body: CLINICIAN_ORIENTATION_COPY,
  },
  patient: {
    title: "Patient setup",
    body: PATIENT_ORIENTATION_COPY,
  },
};

export default function StepCategory({ step, total, value, onChange, onBack, onContinue, lockedRole = "", publicAdmission = false, busy = false, notice = null }) {
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { t, languageCode } = useLanguage();
  const fr = String(languageCode).startsWith('fr');
  const userId = auth?.user?.id || "";
  const categories = [
    {
      key: "doctor",
      title: t("onboarding.category.options.doctor.title", "Clinician"),
      desc: t(
        "onboarding.category.options.doctor.desc",
        "Join as a licensed physician, psychiatrist, nurse practitioner, physician assistant, or other qualified healthcare professional and provide expert care on AfriMed™."
      ),
      icon: "/icons/doctor1.svg",
    },
    {
      key: "patient",
      title: t("onboarding.category.options.patient.title", "Patient"),
      desc: t(
        "onboarding.category.options.patient.desc",
        "Sign up to access trusted clinicians, your electronic records and electronic prescriptions."
      ),
      icon: "/icons/patient1.svg",
    },
    {
      key: "pharmacy",
      invitationRequired: true,
      title: t("onboarding.category.options.pharmacy.title", "Pharmacy"),
      desc: t(
        "onboarding.category.options.pharmacy.desc",
        "Join as a pharmacy to process clinician-issued prescriptions, manage stock, and support pickup or delivery."
      ),
      accessNote: t(
        "onboarding.category.options.pharmacy.invitationRequired",
        "Access is provided through a governed pharmacy invitation."
      ),
      icon: "/icons/pharmacy/pharmacy-onb.svg",
    },
    {
      key: "research",
      invitationRequired: true,
      title: t("onboarding.category.options.research.title", "Research • Public health"),
      desc: t(
        "onboarding.category.options.research.desc",
        "Join as a researcher, public-health professional, community health worker, programme staff or field team member to support governed research and population-health programmes on AfriMed™."
      ),
      accessNote: t(
        "onboarding.category.options.research.invitationRequired",
        "Access is provided through a governed Research • Public health programme or Institution invitation."
      ),
      icon: "/icons/research.svg",
    },
    {
      key: "moh",
      governedAccess: true,
      title: t("onboarding.category.options.moh.title", "Ministry of Health"),
      desc: t(
        "onboarding.category.options.moh.desc",
        "Advance Universal Health Coverage by onboarding citizens on AfriMed™."
      ),
      accessNote: t(
        "onboarding.category.options.moh.governedAccess",
        "Access is provided through a governed Institution or Ministry of Health pathway."
      ),
      icon: "/icons/government.svg",
    },
    {
      key: "employer",
      temporarilyDisabled: true,
      title: t("onboarding.category.options.employer.title", "Employer"),
      desc: t(
        "onboarding.category.options.employer.desc",
        "Give your employees access to quality healthcare. A healthy workforce powers a healthy organisation."
      ),
      icon: "/icons/employer.svg",
    },
    {
      key: "insurers",
      temporarilyDisabled: true,
      title: t("onboarding.category.options.insurers.title", "Insurers"),
      desc: t(
        "onboarding.category.options.insurers.desc",
        "Empower your members with 24/7 access to virtual care while significantly reducing overhead, redundant claims, and emergency room over-utilisation."
      ),
      icon: "/icons/insurance.svg",
    },
  ];

  // Controlled when BOTH value and onChange are provided
  const isControlled = typeof value !== "undefined" && typeof onChange === "function";
  const [local, setLocal] = useState("");
  const [orientationCategory, setOrientationCategory] = useState(null);
  const sel = isControlled ? value : local;
  const orientationCopy = ORIENTATION_DIALOG_COPY[orientationCategory] || null;
  const selectableCats = useMemo(() => {
    if (publicAdmission) return new Set(PUBLIC_ACCOUNT_ROLES);
    const locked = String(lockedRole || "").trim();
    if (locked && CATS.has(locked) && categoryVisible(locked)) return new Set([locked]);
    return new Set(
      [...CATS].filter(
        (key) =>
          !TEMP_DISABLED_CATS.has(key) &&
          !INVITATION_REQUIRED_CATS.has(key) &&
          !GOVERNED_ACCESS_CATS.has(key),
      ),
    );
  }, [lockedRole, publicAdmission]);

  const select = (k) => {
    if (busy || !selectableCats.has(k)) return;
    isControlled ? onChange(k) : setLocal(k);
  };

  const valid = useMemo(() => selectableCats.has(sel), [sel, selectableCats]);
  const persistRoleSelection = async (nextRole) => {
    if (nextRole === "doctor" || nextRole === "patient" || nextRole === "pharmacy") {
      writeLastRole(nextRole, userId);
      const url = new URL(window.location.href);
      url.searchParams.set("role", nextRole);
      window.history.replaceState({}, "", url);
    }
    if (nextRole === "patient" && userId) {
      try {
        sessionStorage.setItem(PATIENT_SELECTION_SESSION_KEY, userId);
      } catch {
        // Session storage is best-effort only.
      }
    }
    if (nextRole === "pharmacy" && userId) {
      await persistPharmacyOnboardingIdentity({
        userId,
        email: auth?.user?.email || auth?.profile?.email || "",
        roleHint: "pharmacy",
        source: "step_category",
        nextRoute: "/onboarding/pharmacy",
        eventName: "pharmacy_role_selected",
      });
    }
  };

  const continueWithSelection = async () => {
    if (!valid || busy) return;
    // A controlled staging campaign has already resolved the lane on the
    // server. Do not mirror it into a mutable URL or browser role cache.
    if (!lockedRole && !publicAdmission) await persistRoleSelection(sel);
    onContinue?.(sel);
  };

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        rootClassName="sm:min-h-0 sm:flex-1"
        bodyClassName="sm:pb-0"
        footerShellClassName="bg-[rgb(var(--clr-surface-container-high))] shadow-[0_-8px_24px_rgba(15,23,42,0.08)] sm:static sm:bg-transparent sm:shadow-none"
        footer={
          <Button
            size="lg"
            disabled={!valid || busy}
            onClick={() => {
              if (!publicAdmission && (sel === "doctor" || sel === "patient")) {
                setOrientationCategory(sel);
                return;
              }
              continueWithSelection();
            }}
          >
            <span className="text-title-medium">
              {t("onboarding.category.continue", "Continue")}
            </span>
          </Button>
        }
      >
        <div className="space-y-3">
          {notice}
          {publicAdmission && <p className="text-body-medium text-on-surface-var">{fr ? 'Choisissez votre parcours. Les services cliniques dépendent de votre profil, des vérifications requises et de leur disponibilité.' : 'Choose your journey. Clinical services depend on your profile, required verification and availability.'}</p>}
          {categories.filter(c => categoryVisible(c.key, publicAdmission)).map((c) => {
            const selected = sel === c.key;
            const publicCopy = publicAdmission ? ({
              doctor: { title: fr ? 'Clinicien' : 'Clinician', desc: fr ? 'Commencez votre profil professionnel. L’accès aux services cliniques dépend des vérifications requises.' : 'Begin your professional profile. Access to clinical services depends on the required verification.' },
              patient: { title: 'Patient', desc: fr ? 'Créez votre profil personnel et suivez les étapes de configuration du compte.' : 'Create your personal profile and follow the account setup steps.' },
            })[c.key] : null;
            const disabled = busy || !selectableCats.has(c.key);
            return (
              <div key={c.key}>
                <Card
                  role={disabled ? undefined : "radio"}
                  tabIndex={disabled ? -1 : 0}
                  aria-disabled={disabled}
                  aria-checked={disabled ? undefined : selected}
                  onClick={() => select(c.key)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); select(c.key); } }}
                  className={[
                    "relative w-full rounded-[12px] border px-4 py-3 text-left flex items-start gap-4 pr-16 shadow-[var(--elev-1)] transition",
                    disabled
                      ? "cursor-not-allowed border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgba(var(--clr-surface-container-high),0.76)] opacity-75"
                      : "border-[rgba(var(--clr-outline-variant),0.95)] bg-[rgb(var(--clr-surface-cont-highest))]",
                  ].join(" ")}
                  style={{ WebkitTapHighlightColor: "transparent", touchAction: "manipulation" }}
                >
                  <div className="mt-[2px] flex h-10 w-10 shrink-0 items-center justify-center">
                    <img src={c.icon} alt="" className="h-10 w-10 shrink-0 object-contain" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-body-large text-on-surface">{publicCopy?.title || c.title}</div>
                      {!selectableCats.has(c.key) ? (
                        <span className="inline-flex items-center rounded-full border border-[rgba(var(--clr-outline-variant),0.7)] bg-[rgba(var(--clr-on-surface),0.04)] px-2 py-0.5 text-label-small uppercase tracking-[0.08em] text-on-surface-var">
                          {lockedRole
                            ? t("onboarding.category.locked", "Assigned access")
                            : c.invitationRequired
                              ? t("onboarding.category.invitationRequired", "Invitation required")
                              : c.governedAccess
                                ? t("onboarding.category.governedAccess", "Governed access")
                            : t("onboarding.category.comingSoon", "Coming soon")}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-body-medium text-on-surface-var">{publicCopy?.desc || c.desc}</p>
                    {disabled && c.accessNote ? (
                      <p className="mt-1 text-body-small text-on-surface-var">{c.accessNote}</p>
                    ) : null}
                  </div>

                  {/* Radio */}
                  <span
                    aria-hidden
                    className={[
                      "pointer-events-none absolute right-4 top-3 grid place-items-center rounded-full h-6 w-6",
                      disabled
                        ? "border border-[rgba(var(--clr-outline),0.55)]"
                        : "",
                      selected
                        ? "border-2 border-[rgb(var(--afrimed-primary))]"
                        : "border border-[rgb(var(--clr-outline))]",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "h-2.5 w-2.5 rounded-full",
                        selected
                          ? "bg-[rgb(var(--afrimed-primary))]"
                          : "bg-transparent",
                      ].join(" ")}
                    />
                  </span>
                </Card>
              </div>
            );
          })}
          <div
            aria-hidden="true"
            className="h-[calc(128px+env(safe-area-inset-bottom))] sm:h-0"
          />
        </div>
      </OnboardingSection>
      {orientationCopy ? (
        <div className="fixed inset-0 z-[260] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/35" />
          <BasicDialogFrame
            role="dialog"
            aria-modal="true"
            title={orientationCopy.title}
            body={orientationCopy.body}
          >
            <button
              type="button"
              className={`${BASIC_DIALOG_ACTION_CLASS} ${BASIC_DIALOG_SECONDARY_ACTION_CLASS}`}
              onClick={continueWithSelection}
            >
              Understood
            </button>
          </BasicDialogFrame>
        </div>
      ) : null}
    </div>
  );
}
